import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import Template from './template';
import * as S from './style';
import moment from 'moment';
import { Label, SignUp, SpaceDiv } from 'components';
import { SignupState, SignupAction } from 'stores/PartnerSignUpStore';
import { defaultState } from 'stores/PartnerSignUpStore/reducer';
import { useAuthVerify } from 'hooks';
import { apiGetPolicy } from 'api/ZimssaAPI';
import { SURVEY } from 'router/PageUrl';
import {
  getYear,
  getMonth,
  getDays,
  validateInput,
  zeroPad,
  validatePhone,
  validateName,
  validateLength,
} from 'commons/ZimssaHelper';
import {
  ALERT_NAME,
  TITLE_PARTNER_SIGNUP,
  BUTTON_NEXT,
  PLACEHOLDER_NAME,
  SELECT_DEFAULT_YEAR,
  SELECT_DEFAULT_MONTH,
  SELECT_DEFAULT_DAY,
} from 'commons/ZimssaString';

interface MainForm {
  year: number;
  month: number;
  day: number;
  isTermsAgreement: boolean;
  isPrivacyAgreement: boolean;
  isActive: boolean;
  isComplete: boolean;
}

function PartnerSignUpView(): React.ReactElement {
  const history = useHistory();
  const dispatcher = useContext(SignupAction);
  const { name, phone, birthday, authCode } = useContext(SignupState);
  const {
    timer,
    isSend,
    isReSend,
    isVerify,
    sendSMSCallback,
    authVerifyCallback,
  } = useAuthVerify();
  const [partnerPolicy, setPartnerPolicy] = useState<{
    terms: string;
    privacy: string;
  }>({
    terms: 'https://www.zimssa.com/partner/policy/terms',
    privacy: 'https://www.zimssa.com/partner/policy/privacy',
  });
  const [mainForm, setMainForm] = useState<MainForm>({
    year: 0,
    month: 0,
    day: 0,
    isTermsAgreement: false,
    isPrivacyAgreement: false,
    isActive: false,
    isComplete: false,
  });

  // state 초기화
  useEffect(() => {
    dispatcher({
      type: 'SET_STATE',
      value: defaultState,
    });
    apiGetPolicy().then((response) => {
      const { result, data } = response;
      if (result === 'success') {
        const responseData: Array<{
          key: string;
          value: string;
        }> = data;

        const terms = responseData.find(
          (item) => item.key === 'partner_policy_terms'
        )?.value;
        const privacy = responseData.find(
          (item) => item.key === 'partner_policy_private'
        )?.value;

        setPartnerPolicy({
          terms: terms ? terms : partnerPolicy.terms,
          privacy: privacy ? privacy : partnerPolicy.privacy,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 일자 조회
  useEffect(() => {
    if (
      validateInput(mainForm.year) &&
      validateInput(mainForm.month) &&
      validateInput(mainForm.day)
    ) {
      dispatcher({
        type: 'birthday',
        value: moment(
          new Date(
            `${mainForm.year}-${zeroPad(mainForm.month)}-${zeroPad(
              mainForm.day
            )}`
          )
        ).format('YYMMDD'),
      });
    }
  }, [mainForm.year, mainForm.month, mainForm.day, dispatcher]);

  // 인증 하기
  const onClickVerifyCallback = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      return authVerifyCallback(phone, authCode, 'partner-web-signup');
    },
    [phone, authCode, authVerifyCallback]
  );

  const onChangeCallback = useCallback(
    (type: any, value: string) => {
      setMainForm({
        ...mainForm,
        [type]: Number(value),
      });
    },
    [mainForm]
  );

  const signUpProps = {
    name: {
      caption: validateName(name) ? null : (
        <Label contents={ALERT_NAME} color={'red_900'} fontStyle={'caption'} />
      ),
      inputProps: {
        inputName: 'name',
        placeholder: PLACEHOLDER_NAME,
        isValid: validateName(name),
        maxLength: 20,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          const { value } = e.target;
          dispatcher({
            type: 'name',
            value: value,
          });
        },
      },
    },
    phone: {
      value: phone,
      isActive: (validatePhone(phone) && !isSend) || (validatePhone(phone) && isReSend),
      isSend: isSend,
      onChangeHandler: (type: any, value: string) => {
        dispatcher({
          type: 'phone',
          value: value,
        });
      },
      onClickSendSMS: () => {
        if (phone) {
          sendSMSCallback(phone, 'partner-web-signup');
        }
      },
    },
    authCode: {
      value: authCode,
      timer: timer,
      isVerify: isVerify,
      onClickVerify: onClickVerifyCallback,
      onChangeHandler: (type: any, value: string) => {
        dispatcher({
          type: 'authCode',
          value: value,
        });
      },
    },
    birthdayYear: {
      inputName: 'year',
      children: [{ name: SELECT_DEFAULT_YEAR, value: 0 }, ...getYear(100)],
      onChangeHandler: onChangeCallback,
    },
    birthdayMonth: {
      inputName: 'month',
      children: [{ name: SELECT_DEFAULT_MONTH, value: 0 }, ...getMonth()],
      onChangeHandler: onChangeCallback,
    },
    birthdayDay: {
      inputName: 'day',
      children:
        mainForm.year !== 0 && mainForm.month !== 0
          ? [
              { name: SELECT_DEFAULT_DAY, value: 0 },
              ...getDays(mainForm.year, mainForm.month),
            ]
          : [{ name: SELECT_DEFAULT_DAY, value: 0 }],
      onChangeHandler: onChangeCallback,
    },
    termsAgreement: {
      onClickAgreement: (value: boolean) => {
        setMainForm({
          ...mainForm,
          isTermsAgreement: value,
        });
      },
      onClickLink: (
          e: React.MouseEvent<HTMLLabelElement, globalThis.MouseEvent>
      ) => {
        e.preventDefault();
        e.stopPropagation();

        window.open(partnerPolicy.terms, '_blank');
      },
    },
    privacyAgreement: {
      onClickAgreement: (value: boolean) => {
        setMainForm({
          ...mainForm,
          isPrivacyAgreement: value,
        });
      },
      onClickLink: (
          e: React.MouseEvent<HTMLLabelElement, globalThis.MouseEvent>
      ) => {
        e.preventDefault();
        e.stopPropagation();

        window.open(partnerPolicy.privacy, '_blank');
      },
    },
    btnNext: {
      contents: BUTTON_NEXT,
      isActive:
        validateName(name.trim()) &&
        validatePhone(phone.trim()) &&
        validateInput(birthday.trim()) &&
        validateLength(authCode, 4) &&
        mainForm.isTermsAgreement &&
        mainForm.isPrivacyAgreement &&
        isVerify,
      isComplete: false,
      onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        history.push(SURVEY.path);
      },
    },
  };

  return (
    <Template>
      <S.TitleWrapper>
        <Label
          contents={TITLE_PARTNER_SIGNUP}
          color={'gray_opacity_800'}
          fontStyle={'h3_bold'}
        />
      </S.TitleWrapper>
      <SpaceDiv height={12} />
      <SignUp {...signUpProps} />
    </Template>
  );
}

export default PartnerSignUpView;
